import { useId, type ReactNode } from 'react';
import { cx } from '@/shared/lib/cx';
import s from './Choice.module.css';
import { Fieldset } from './FormField';

export interface RadioOption<V extends string = string> {
  value: V;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
}

export interface RadioGroupProps<V extends string = string> {
  legend: ReactNode;
  options: ReadonlyArray<RadioOption<V>>;
  value: V | null;
  onChange: (value: V) => void;
  name?: string;
  orientation?: 'vertical' | 'horizontal';
  hint?: ReactNode;
  error?: ReactNode;
  disabled?: boolean;
  required?: boolean;
  hideLegend?: boolean;
}

/** Группа радиокнопок в fieldset с legend; стрелки переключают выбор (нативное поведение). */
export function RadioGroup<V extends string = string>({
  legend,
  options,
  value,
  onChange,
  name,
  orientation = 'vertical',
  hint,
  error,
  disabled = false,
  required = false,
  hideLegend = false,
}: RadioGroupProps<V>) {
  const autoId = useId();
  const groupName = name ?? `radio-${autoId}`;
  return (
    <Fieldset legend={legend} hint={hint} error={error} hideLegend={hideLegend}>
      <div className={cx(s.group, orientation === 'horizontal' && s.horizontal)} role="radiogroup" aria-required={required || undefined}>
        {options.map((option) => {
          const optionId = `${groupName}-${option.value}`;
          const labelId = `${optionId}-label`;
          const descriptionId = option.description ? `${optionId}-description` : undefined;
          const isDisabled = disabled || option.disabled;
          return (
            <label key={option.value} className={s.choice} htmlFor={optionId} data-disabled={isDisabled ? 'true' : undefined} data-invalid={error ? 'true' : undefined}>
              <input
                id={optionId}
                className={s.input}
                type="radio"
                name={groupName}
                value={option.value}
                checked={value === option.value}
                disabled={isDisabled}
                required={required}
                aria-labelledby={labelId}
                aria-describedby={descriptionId}
                onChange={() => onChange(option.value)}
              />
              <span className={s.circle} aria-hidden="true" />
              <span className={s.text}>
                <span id={labelId}>{option.label}</span>
                {option.description ? (
                  <span id={descriptionId} className={s.description}>
                    {option.description}
                  </span>
                ) : null}
              </span>
            </label>
          );
        })}
      </div>
    </Fieldset>
  );
}
